import { useEffect } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Zap, Home as HomeIcon, Building2, Leaf, Factory, Layers } from "lucide-react";
import PageHero from "@/components/PageHero";
import CTASection from "@/components/CTASection";
import { Reveal, Eyebrow } from "@/components/motion";
import { setPageMeta } from "@/components/seo";
import { IMAGES } from "@/data/site";

const WHAT_WE_DO = [
  { icon: Zap, label: "Level 2", to: "/services#level2" },
  { icon: HomeIcon, label: "Domestic / Residential", to: "/services#residential" },
  { icon: Building2, label: "Commercial", to: "/services#commercial" },
  { icon: Leaf, label: "Energy Efficiency Solutions", to: "/services#energy-efficiency" },
  { icon: Factory, label: "Industrial", to: "/services#commercial" },
  { icon: Layers, label: "Large Scale Projects", to: "/services#commercial" },
];

const About = () => {
  useEffect(() => {
    setPageMeta(
      "About Us | LAD CO Electrical — Level 2 Electrician Northern Rivers NSW",
      "LAD CO Electrical is a locally owned Level 2 accredited electrician in the Northern Rivers NSW, delivering premium residential, commercial and industrial electrical solutions backed by decades of industry experience."
    );
  }, []);

  return (
    <>
      <PageHero
        eyebrow="About Us"
        title="Local Experience. Reliable Service."
        sub="A local family business with more than 20 years of experience, servicing the Northern Rivers of NSW."
      />

      <section className="bg-white" data-testid="about-story">
        <div className="container-site py-20 lg:py-28 grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16 items-center">
          <Reveal>
            <Eyebrow>Our Story</Eyebrow>
            <h2 className="font-heading text-2xl sm:text-3xl lg:text-4xl font-bold uppercase tracking-tight text-ink mt-4 leading-snug">
              A Local Family Business You Can Rely On
            </h2>
            <p className="text-gray-600 text-base sm:text-lg mt-6 leading-relaxed">
              LAD CO Electrical is a locally owned and operated electrical contracting business delivering premium electrical solutions across residential, commercial, and industrial sectors. We provide all Level 2 ASP services in addition to domestic and commercial work. Backed by decades of industry experience, LAD CO have a reputation for exceptional workmanship, reliability, industry knowledge and attention to detail.
            </p>
            <p className="text-gray-600 text-base sm:text-lg mt-4 leading-relaxed">
              From homes and high end residential developments to commercial fit outs, industrial facilities, and complex projects, our team provides tailored electrical solutions designed to meet high standards and customer satisfaction. We combine proven expertise with the latest technologies, including smart home automation, energy efficiency, and advanced power systems and upgrades.
            </p>
            <p className="text-gray-600 text-base sm:text-lg mt-4 leading-relaxed">
              At LAD CO Electrical, we work closely with builders, developers, businesses, and homeowners to deliver safe, innovative, and expertly executed electrical solutions that stand the test of time.
            </p>
            <div className="grid grid-cols-2 gap-px bg-gray-200 border border-gray-200 mt-10">
              {[
                ["20+", "Years Experience"],
                ["Level 2", "Accredited Service Provider"],
                ["Local", "Family Business"],
                ["3", "Customer Sectors — Res, Com, Ind"],
              ].map(([big, small]) => (
                <div key={small} className="bg-white p-6">
                  <span className="block font-heading text-2xl font-extrabold text-volt">{big}</span>
                  <span className="block font-mono text-[10px] uppercase tracking-[0.2em] text-gray-500 mt-1.5">{small}</span>
                </div>
              ))}
            </div>
          </Reveal>
          <Reveal delay={0.15}>
            <div className="relative">
              <div className="absolute -top-4 -right-4 w-full h-full border-2 border-volt" aria-hidden="true" />
              <img
                src={IMAGES.tempSupply}
                alt="LAD CO Electrical team member in hi-vis workwear completing a site power connection in the Northern Rivers"
                className="relative w-full aspect-[4/3] object-cover"
                loading="lazy"
              />
            </div>
          </Reveal>
        </div>
      </section>

      <section className="bg-[#F4F6F5] border-t border-gray-200" data-testid="about-what-we-do">
        <div className="container-site py-20 lg:py-28">
          <Reveal>
            <Eyebrow>Our Capabilities</Eyebrow>
            <h2 className="font-heading text-2xl sm:text-3xl lg:text-4xl font-bold uppercase tracking-tight text-ink mt-4">
              What We Do
            </h2>
          </Reveal>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 mt-12">
            {WHAT_WE_DO.map((item, i) => (
              <Reveal key={item.label} delay={i * 0.06}>
                <Link
                  to={item.to}
                  className="group bg-white border border-gray-200 p-8 h-full flex flex-col transition-[transform,border-color] duration-300 hover:-translate-y-1.5 hover:border-volt"
                  data-testid={`what-we-do-${i}`}
                >
                  <span className="w-12 h-12 bg-ink flex items-center justify-center transition-colors duration-300 group-hover:bg-volt">
                    <item.icon className="w-5 h-5 text-white" />
                  </span>
                  <h3 className="font-heading text-lg font-bold tracking-tight text-ink mt-6 flex-1">{item.label}</h3>
                  <span className="inline-flex items-center gap-2 font-heading text-xs font-bold uppercase tracking-wider text-volt mt-5 group-hover:gap-3 transition-all duration-300">
                    Learn More <ArrowRight className="w-3.5 h-3.5" />
                  </span>
                </Link>
              </Reveal>
            ))}
          </div>
          <Reveal delay={0.2}>
            <div className="mt-14 flex flex-col sm:flex-row gap-4">
              <Link to="/quote" className="btn-volt" data-testid="about-quote-cta">
                Request a Quote <ArrowRight className="w-4 h-4" />
              </Link>
            </div>
          </Reveal>
        </div>
      </section>

      <CTASection />
    </>
  );
};

export default About;
